import { postJSON } from "@devforce/trailhead-client-fetch";
import { getAccessToken } from "@devforce/trailhead-client-auth";

type InboxResponse = {
  inbox: {
    unreadConversationsCount: {
      unreadCount: number;
      hasMore: boolean;
    };
  };
};

// An api response error or an error thrown programmatically.
type Error = {
  message: string;
  extensions?: {
    code: number;
    statusCode: string;
    clientMessage: string;
  };
};

type ErrorResponse = {
  errors: Error[];
};

const requestBody = {
  operationName: "UnreadConversationsCount",
  query: `query UnreadConversationsCount {
    inbox {
      unreadConversationsCount {
        unreadCount
        hasMore
      }
    }
  }`,
};

// Max count returned from the API.
// If the count exceeds this value, it returns hasMore: true.
export const API_MAX_MESSAGE_COUNT = 50;

function getRequestHeaders(accessToken: string) {
  return {
    headers: {
      authorization: `Bearer ${accessToken}`,
    },
  };
}

// Normalizes api vs non-api errors.
function getNormalizedError(err: ErrorResponse & Error) {
  const errors = (err as ErrorResponse).errors;
  const error: Error = errors && Array.isArray(errors) ? errors[0] : err;

  let message: string = "Unknown error";
  if (error) {
    const msg = error.message;
    const code = error.extensions && error.extensions.statusCode;
    message = (code ? `[${code}] ` : "") + msg;
  }

  return new Error(message);
}

export class InboxNotificationUtil {
  apiUrl: string = null!;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  async getUnreadMessageCount(): Promise<number> {
    try {
      const { data } = await this.getInboxResponse();
      const { unreadCount, hasMore } = data.inbox.unreadConversationsCount;
      return unreadCount === API_MAX_MESSAGE_COUNT && hasMore
        ? API_MAX_MESSAGE_COUNT + 1
        : unreadCount;
    } catch (e) {
      throw getNormalizedError(e as ErrorResponse & Error);
    }
  }

  private async getInboxResponse(): Promise<{ data: InboxResponse }> {
    const accessToken = await getAccessToken();
    return accessToken
      ? this.doRequest(accessToken)
      : Promise.reject(new Error("Unable to get access token"));
  }

  private doRequest(accessToken: string): Promise<{ data: InboxResponse }> {
    // Make gql request manually since servers aren't federated yet.
    // TODO: revisit this logic when federation is in place.
    return postJSON<{ data: InboxResponse }>(
      this.apiUrl,
      requestBody,
      getRequestHeaders(accessToken)
    );
  }
}
